import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { OfficineSelectorComponent } from '../shared/components/officine-selector/officine-selector.component';
import { MatTabsModule } from '@angular/material/tabs'; 
import { MatToolbarModule } from '@angular/material/toolbar';
import { GenericManufacturerTableComponent } from './components/generic-manufacturer-table/generic-manufacturer-table.component';
import { YearsSelectorComponent } from '../shared/components/years-selector/years-selector.component';
import { MonthsSelectorComponent } from '../shared/components/months-selector/months-selector.component';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  retry,
  Subject,
  switchMap,
  takeUntil,
  tap,
  distinctUntilChanged,
} from 'rxjs';
import { UserSessionService } from '../shared/services/user.service';
import { Adherent } from '../shared/interfaces/BDD-Ref/adherent';
import { SimulationsBySupplier } from '../shared/objects/simulations-by-supplier';
import { GenericManufacturerSimulationService } from '../shared/services/generic-manufacturer-simulation.service';
import { CommonModule } from '@angular/common';
import { SnackbarService } from '../shared/services/snackbar/snackbar.service';
import { GenericManufacturerSimulation } from '../shared/objects/generic-manufacturer-simulation';
import { GenericManufacturerSimulationGlobalInformation } from '../shared/objects/generic-manufacturer-simulation-global-information';
import { FilialeSelectorComponent } from '../shared/components/filiale-selector/filiale-selector.component';

@Component({
  selector: 'app-generic-manufacturer-simulation',
  standalone: true,
  templateUrl: './generic-manufacturer-simulation.component.html',
  styleUrl: './generic-manufacturer-simulation.component.scss',
  imports: [
    CommonModule,
    OfficineSelectorComponent,
    FilialeSelectorComponent,
    MonthsSelectorComponent,
    YearsSelectorComponent,
    MatTabsModule,
    MatToolbarModule,
    GenericManufacturerTableComponent,
  ],
})
export class GenericManufacturerSimulationComponent implements OnInit, OnDestroy {
  private readonly _destroying$: Subject<void> = new Subject<void>();

  readonly selectedYear$ = new BehaviorSubject<number | undefined>(undefined);
  readonly selectedMonths$ = new BehaviorSubject<string[] | undefined>(undefined);
  readonly adherent$ = new BehaviorSubject<Adherent | undefined>(undefined);
  readonly genericManufacturer$ = new BehaviorSubject<
    SimulationsBySupplier<
      GenericManufacturerSimulation,
      GenericManufacturerSimulationGlobalInformation
    >[]
  >([]);
  availableYears: number[] | undefined = undefined;
  availableMonths: string[] | undefined = undefined;
  private _simulationsBackup: any[] = [];
  activiteAdherent!:string;

  constructor(
    private readonly _userService: UserSessionService,
    private readonly _genericManufacturerSimulationService: GenericManufacturerSimulationService,
    private readonly _snackBarService: SnackbarService,
   private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this._userService
      .getSelectedAdherent()
      .pipe(
        distinctUntilChanged((a, b) => (a?.idAdherent ?? null) === (b?.idAdherent ?? null)),
        takeUntil(this._destroying$),
        tap((adherent: Adherent | null) => {
          if (adherent) {
            this.setAdherent(adherent);
            this.initializeMonthsAndYears(adherent);
            this.filterSimulations();
          }
        }),
      )
      .subscribe();
      this.activiteAdherent = this._userService.getActivite();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  private filterSimulations(): void {
    combineLatest([this.adherent$.pipe(distinctUntilChanged()), this.selectedYear$.pipe(distinctUntilChanged())])
      .pipe(
        // compare les idReferentiel des deux adherents pour ne pas spammer l'api
        distinctUntilChanged(
          ([adherentA, yearA], [adherentB, yearB]) =>
            (adherentA?.idReferentiel ?? null) === (adherentB?.idReferentiel ?? null) &&
            yearA === yearB
        ),
        filter(([adherent]) => !!adherent && adherent.idReferentiel > 0),
        switchMap(([adherent, year]) => {
          const id = adherent!.idReferentiel;

          const request$ =
            year && year > 0
              ? this._genericManufacturerSimulationService.getGenericSimulationByAdherentAndYear(
                  id,
                  year,
                )
              : this._genericManufacturerSimulationService.getGenericSimulationByAdherent(id);

          return request$.pipe(
            catchError(error => {
              console.error('Erreur lors de la récupération des remises génériques :', error);
              this._snackBarService.show(
                error?.error ?? 'Une erreur est survenue. Veuillez réessayer plus tard.',
                'snackbar-error',
              );
              return of([]);
            }),
          );
        }),
        tap((response: any[]) => {
          // Sauvegarde dans le backup pour filtres ultérieurs
          this._simulationsBackup = response;
        }),
        switchMap(() => this.selectedMonths$), // on attend le dernier selectedMonths$
        map(selectedMonths => {
          if (!Array.isArray(this._simulationsBackup)) return [];
          return (
            this._simulationsBackup
              .map((item: any) => {
                // 1. Filtrer les simulations selon les mois sélectionnés
                const filteredSimulations = item.simulations.filter((sim: any) =>
                  selectedMonths?.some(m => m.startsWith(sim.mois?.toString() + ' - ')),
                );
                // Étape 1.1 : filtrer les simulations Globales selon les mois sélectionnés
                const filteredSimulationGlobalInformation = item.simulationGlobalInformation.filter(
                  (info: any) =>
                    selectedMonths?.some(m => m.startsWith(info.mois?.toString() + ' - ')) &&
                    info.annee === this.selectedYear$.value,
                );

                // 2. Regrouper par champ `assiette` (backend de `base`)
                const groupedMap: Record<string, any> = {};

                for (const sim of filteredSimulations) {
                  const baseKey = sim.assiette || 'UNKNOWN';

                  if (!groupedMap[baseKey]) {
                    groupedMap[baseKey] = {
                      ...sim,
                      quantite: sim.quantite || 0,
                      montantBrutHt: sim.montantBrutHt || 0,
                      remiseEuroHt: sim.remiseEuroHt || 0,
                      montantNetHt: sim.montantNetHt || 0,
                    };
                  } else {
                    groupedMap[baseKey].quantite += sim.quantite || 0;
                    groupedMap[baseKey].montantBrutHt += sim.montantBrutHt || 0;
                    groupedMap[baseKey].remiseEuroHt += sim.remiseEuroHt || 0;
                    groupedMap[baseKey].montantNetHt += sim.montantNetHt || 0;
                  }
                }

                const groupedSimulations = Object.values(groupedMap);

                return {
                  id: crypto.randomUUID(),
                  supplierName: item.manufacturerName,
                  simulations: GenericManufacturerSimulation.fromBackendList(groupedSimulations),
                  simulationGlobalInformation:
                    GenericManufacturerSimulationGlobalInformation.fromBackendList(
                      filteredSimulationGlobalInformation,
                    ),
                };
              })
              // Ajoute ce filtre pour ne rien renvoyer si les deux tableaux sont vides (pour afficher Aucune remise générique/grossiste trouvée
              .filter(
                (obj: any) =>
                  (obj.simulations && obj.simulations.length > 0) ||
                  (obj.simulationGlobalInformation && obj.simulationGlobalInformation.length > 0),
              )
          );
        }),

        tap(sorted => {
          this.genericManufacturer$.next(sorted);
        }),
        takeUntil(this._destroying$),
      )
      .subscribe();
  }

  /**
   * Get a unique key for the simulation, allowing to use it in a trackBy function
   * @param simulation for which we want to get the tracking key
   * @returns a unique key for the simulation
   */
  trackingKey(
    index: number,
    simulation: SimulationsBySupplier<
      GenericManufacturerSimulation,
      GenericManufacturerSimulationGlobalInformation
    >,
  ): string {
    return simulation.id;
  }

  onYearSelectionChange(value: number): void {
    // Sécurité NG0100 : tout dans un setTimeout (prochain tick)
    setTimeout(() => {
      this.selectedYear$.next(value);
      const currentYear = new Date().getFullYear();
      const currentMonthIndex = new Date().getMonth();
      this.getAvailableMonths(); // met à jour this.availableMonths

      // si pas de mois disponibles
      if (!this.availableMonths || this.availableMonths.length === 0) {
        return;
      }

      if (value === currentYear) {
        // année en cours > seulement le dernier mois
        const lastMonth = this.availableMonths[currentMonthIndex - 1];
        // uniquement si lastMonth est défini
        if (lastMonth) {
          this.selectedMonths$.next([lastMonth]);
        }
      } else {
        // autre année > tous les mois
        this.selectedMonths$.next([...this.availableMonths]);
      }
    }, 0);
  }

  onMonthSelectionChange(value: any): void {
    setTimeout(() => {
      this.selectedMonths$.next(value);
    }, 0);
  }

  private setAdherent(adherent: Adherent): void {
    // Forcer la réinitialisation des mois
    this.adherent$.next(adherent);
  }

  private initializeMonthsAndYears(adherent: Adherent): void {
    this._genericManufacturerSimulationService
      .getAvailableYearsByAdherent(adherent.idReferentiel)
      .pipe(
        //compare la fraicheur des données pour ne pas spammer l'api
        distinctUntilChanged((a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this._destroying$),
        tap((years: number[]) => {
          const hasYears = years && years.length > 0;
          const listOfYears = hasYears ? years : undefined;
          const isJanuary = new Date().getMonth() === 0;
          let yearToUse: number | undefined = undefined;
          if (hasYears) {
            if (isJanuary && years.length > 1) {
              // Prendre l'année la plus récente qui est < année courante
              yearToUse = Math.max(...years.filter(y => y < new Date().getFullYear()));
            } else {
              yearToUse = Math.max(...years);
            }
          }

          // fix erreur NG0100 : décalage du changement après le cycle courant
          setTimeout(() => {
              this.availableYears = listOfYears;
              this.getAvailableMonths();
              this.selectedYear$.next(yearToUse);
              this.onYearSelectionChange(yearToUse!);
              this.cdr.detectChanges(); // optionnel mais sûr
          }, 0);
        }),
      )
      .subscribe();
  }

  private getAvailableMonths(): void {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const months: string[] = [];

    for (let i = 1; i <= (this.selectedYear$.value === currentYear ? currentMonth - 1 : 12); i++) {
      months.push(i + ' - ' + new Date(0, i - 1).toLocaleString('fr', { month: 'long' }));
    }

    // Mettre à jour availableMonths directement, Angular gère la détection de changement
    this.availableMonths = months;
  }
}
